/**
 * Deletion Requests inbox + Recycle Bin views (embedded in SubmissionsPage).
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, RotateCcw, Trash2 } from 'lucide-react';

const stColors = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

export function DeletionRequestsView({ isAdmin, onChanged }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/deletion-requests', { params: { status: filter } });
      setRows(r.data.requests);
    } catch { }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (rid, action, body) => {
    try {
      const r = await api.post(`/deletion-requests/${rid}/${action}`, body || {});
      toast.success(r.data.message);
      setRejecting(null); setNote('');
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  return (
    <Card className="border border-border rounded-sm shadow-sm" data-testid="deletion-requests-view">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          {['pending', 'approved', 'rejected', 'all'].map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`dr-filter-${f}`}
              className={`text-[11px] px-2.5 py-1 rounded-sm border capitalize ${filter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600'}`}>{f}</button>
          ))}
          <span className="text-[11px] text-slate-400 ml-auto">{isAdmin ? 'Approve moves submissions to the Recycle Bin (30-day restore) · Reject keeps data untouched' : 'Your deletion requests — pending admin approval'}</span>
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="dr-empty">No {filter !== 'all' ? filter : ''} deletion requests</p>
        ) : rows.map((r) => (
          <div key={r.id} className={`border rounded-sm p-3 ${r.status === 'pending' ? 'border-amber-200 bg-amber-50/40' : 'border-slate-100'}`} data-testid={`dr-row-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className={`text-[10px] rounded-sm ${stColors[r.status]}`}>{r.status}</Badge>
              <span className="text-sm font-medium text-slate-800">{r.requested_by_name}</span>
              <span className="text-xs text-slate-400">requested deleting <b>{r.target_ids.length}</b> submission(s) · {new Date(r.requested_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              {isAdmin && r.status === 'pending' && (
                <span className="ml-auto flex gap-1.5">
                  <Button size="sm" className="h-7 rounded-sm text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(r.id, 'approve')} data-testid={`dr-approve-${r.id}`}><CheckCircle className="w-3 h-3 mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs text-red-600 border-red-200" onClick={() => setRejecting(r.id)} data-testid={`dr-reject-${r.id}`}><XCircle className="w-3 h-3 mr-1" />Reject</Button>
                </span>
              )}
              {!isAdmin && r.status === 'pending' && (
                <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs ml-auto" onClick={() => act(r.id, 'cancel')} data-testid={`dr-cancel-${r.id}`}>Cancel request</Button>
              )}
            </div>
            <p className="text-xs text-slate-600 mt-1.5"><b>Reason:</b> {r.reason}</p>
            <div className="mt-1.5 space-y-0.5">
              {r.previews.slice(0, 5).map((p) => (
                <p key={p.id} className="text-[11px] text-slate-500 truncate">• {p.form_name} · {p.snippet || p.id}</p>
              ))}
              {r.previews.length > 5 && <p className="text-[11px] text-slate-400">…and {r.previews.length - 5} more</p>}
            </div>
            {r.decided_by_name && <p className="text-[11px] text-slate-400 mt-1.5">{r.status} by {r.decided_by_name} · {new Date(r.decided_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}{r.decision_note ? ` — "${r.decision_note}"` : ''}</p>}
            {rejecting === r.id && (
              <div className="mt-2 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Rejection note (optional)" className="flex-1 text-xs border border-slate-200 rounded-sm px-2 py-1.5" data-testid="dr-reject-note" />
                <Button size="sm" className="h-7 rounded-sm text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => act(r.id, 'reject', { note })} data-testid="dr-reject-confirm">Confirm reject</Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function RecycleBinView({ onChanged }) {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try { const r = await api.get('/recycle-bin'); setItems(r.data.items); } catch { }
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    try { const r = await api.post(`/recycle-bin/${id}/restore`); toast.success(r.data.message); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
  };
  const purge = async (id) => {
    if (!window.confirm('Permanently delete this submission? This cannot be undone.')) return;
    try { const r = await api.delete(`/recycle-bin/${id}`); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Purge failed'); }
  };

  return (
    <Card className="border border-border rounded-sm shadow-sm" data-testid="recycle-bin-view">
      <CardContent className="p-4 space-y-2">
        <p className="text-[11px] text-slate-400">Deleted submissions are kept for <b>30 days</b>, then purged automatically. Restore puts them back exactly as they were.</p>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="bin-empty">Recycle bin is empty</p>
        ) : items.map((it) => {
          const purgeAt = new Date(new Date(it.deleted_at).getTime() + 30 * 864e5);
          return (
            <div key={it.id} className="border border-slate-100 rounded-sm p-3 flex flex-wrap items-center gap-2" data-testid={`bin-row-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800">{it.form_name} <Badge variant="outline" className="text-[10px] rounded-sm ml-1">{it.form_type}</Badge></div>
                <p className="text-[11px] text-slate-500 truncate">{it.preview?.snippet || it.id}</p>
                <p className="text-[11px] text-slate-400">deleted by {it.deleted_by_name} · {new Date(it.deleted_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · auto-purges {purgeAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
              </div>
              <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => restore(it.id)} data-testid={`bin-restore-${it.id}`}><RotateCcw className="w-3 h-3 mr-1" />Restore</Button>
              <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs text-red-600 border-red-200" onClick={() => purge(it.id)} data-testid={`bin-purge-${it.id}`}><Trash2 className="w-3 h-3 mr-1" />Purge now</Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
